import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Users, Clock, PlayCircle } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../../firebase-config";
import { useAuth } from "../../../../provider/Auth.provider";
import { notification } from "antd";

const goldGrad =
  "bg-gradient-to-r from-[#B17E32] via-[#F3E681] to-[#CF9F41] text-black";

function GhostButton({ icon: Icon, children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={`inline-flex items-center gap-2 rounded-md border border-[#333] px-3 py-2 text-sm text-white/90 hover:bg-white/5 transition ${className}`}
    >
      {Icon ? <Icon className="h-4 w-4" /> : null}
      {children}
    </button>
  );
}

function StatTile({ icon: Icon, value, label }) {
  return (
    <div className="flex items-center gap-3 rounded-lg bg-[#2A2A2A] p-3">
      <Icon className="h-5 w-5 text-[#CF9F41]" />
      <div className="leading-tight">
        <div className="text-base font-bold text-[#CF9F41]">{value}</div>
        <div className="text-xs text-gray-300">{label}</div>
      </div>
    </div>
  );
}

const CourseList = ({ setCourseId }) => {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [packageData, setPackageData] = useState(null);
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPackageAndCourses = async () => {
      if (!packageId) {
        navigate("/student/dashboard");
        return;
      }

      try {
        const packageRef = doc(db, "lms_package", packageId);
        const packageSnap = await getDoc(packageRef);

        if (!packageSnap.exists()) {
          notification.error({
            message: "Package not found",
            placement: "topRight",
          });
          navigate("/student/dashboard");
          return;
        }

        const info = packageSnap.data();
        setPackageData(info);

        // Fetch courses (sequential to preserve order)
        if (
          Array.isArray(info.packageContainingCourses) &&
          info.packageContainingCourses.length
        ) {
          const all = [];
          for (const courseId of info.packageContainingCourses) {
            const ref = doc(db, "lms_course", courseId);
            const snap = await getDoc(ref);
            if (snap.exists()) all.push({ id: snap.id, ...snap.data() });
          }
          setCourses(all);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        notification.error({
          message: "Error loading courses",
          placement: "topRight",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPackageAndCourses();
  }, [packageId, navigate]);

  const handleCourseClick = async (courseId) => {
    try {
      const courseRef = doc(db, "lms_course", courseId);
      const snap = await getDoc(courseRef);
      if (snap.exists()) {
        const data = snap.data();
        if (
          Array.isArray(data.courseContainingModules) &&
          data.courseContainingModules.length
        ) {
          const firstModuleId = data.courseContainingModules[0];
          navigate(
            `/student/dashboard/video/${firstModuleId}?courseId=${courseId}`
          );
        } else {
          notification.error({
            message: "No modules found",
            description: "This course doesn't have any modules yet.",
            placement: "topRight",
          });
        }
      }
    } catch (err) {
      console.error("Navigation error:", err);
      notification.error({
        message: "Error",
        description: "Unable to load course content.",
        placement: "topRight",
      });
    }
  };

  // Learning points cleaner → 2 columns
  const [leftCol, rightCol] = useMemo(() => {
    const raw = packageData?.learningPoints;
    let points = raw || "";
    if (
      typeof points !== "string" ||
      points.length < 10 ||
      /xyzkm|llllllllll/i.test(points)
    ) {
      points = [
        "Modern development fundamentals",
        "Best practices and patterns",
        "Real-world project implementation",
        "Advanced techniques",
        "Industry-standard tools",
        "Professional development skills",
      ];
    } else {
      points = points
        .replace(/<p[^>]*>/gi, "")
        .replace(/<\/p>/gi, "\n")
        .replace(/<li[^>]*>/gi, "")
        .replace(/<\/li>/gi, "\n")
        .replace(/<ul[^>]*>/gi, "")
        .replace(/<\/ul>/gi, "")
        .replace(/<ol[^>]*>/gi, "")
        .replace(/<\/ol>/gi, "")
        .replace(/<br\s*\/?>(?=\s*\n?)/gi, "\n")
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+\n/g, "\n")
        .trim()
        .split(/\n+/)
        .map((p) => p.replace(/^[•*\-]\s*/, "").trim())
        .filter(Boolean);
      if (!points.length) {
        points = [
          "Modern development fundamentals",
          "Best practices and patterns",
          "Real-world project implementation",
          "Advanced techniques",
          "Industry-standard tools",
          "Professional development skills",
        ];
      }
    }
    const mid = Math.ceil(points.length / 2);
    return [points.slice(0, mid), points.slice(mid)];
  }, [packageData?.learningPoints]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black p-4 text-white">
        <div className="mx-auto flex max-w-6xl items-center justify-center py-24">
          <div className="flex flex-col items-center gap-4">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#2A2A2A] border-t-[#CF9F41]" />
            <p className="text-sm text-gray-300">Loading courses…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!packageData) {
    return (
      <div className="min-h-screen bg-black p-4 text-white">
        <div className="mx-auto max-w-3xl rounded-lg border border-[#2A2A2A] bg-[#111111] p-6">
          <h2 className="mb-2 text-lg font-semibold text-[#CF9F41]">
            Package not found
          </h2>
          <GhostButton
            icon={ArrowLeft}
            onClick={() => navigate("/student/dashboard")}
          >
            Back to My Packages
          </GhostButton>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-4 text-white">
      {/* Back Button */}
      <div className="mx-auto mb-6 max-w-6xl">
        <GhostButton
          icon={ArrowLeft}
          onClick={() => navigate("/student/dashboard")}
        >
          Back to My Packages
        </GhostButton>
      </div>

      {/* Header hero */}
      <div className="mx-auto mb-10 max-w-6xl rounded-xl border border-[#2A2A2A] bg-[#0F0F0F] p-4 sm:p-6">
        {/* Title row */}
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">{packageData.packageName}</h1>
            <p className="mt-1 text-sm text-gray-400">
              {packageData.packageDifficulty} Digital Development Course
            </p>
          </div>
          <span
            className={`h-fit rounded-full px-3 py-1 text-xs font-bold ${goldGrad}`}
          >
            DIGITAL
          </span>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Left: image */}
          <div>
            <div className="overflow-hidden rounded-lg">
              <img
                src={
                  packageData.packageBanner ||
                  "https://via.placeholder.com/640x360/1f2937/ffffff?text=Package"
                }
                alt={packageData.packageName}
                className="h-56 w-full object-cover"
                onError={(e) => {
                  e.currentTarget.src =
                    "https://via.placeholder.com/640x360/1f2937/ffffff?text=Package";
                }}
              />
            </div>
          </div>

          {/* Right: description + stats */}
          <div className="flex flex-col gap-6">
            <div>
              <h3 className="mb-2 text-sm font-semibold text-[#CF9F41]">
                Course Description
              </h3>
              <p className="text-sm text-gray-300 leading-6">
                {(() => {
                  let description =
                    packageData.packageDescription ||
                    "Master modern development skills in this comprehensive digital course.";
                  description = description
                    .replace(/<[^>]*>/g, "")
                    .replace(/&nbsp;/g, " ")
                    .replace(/&amp;/g, "&")
                    .replace(/&lt;/g, "<")
                    .replace(/&gt;/g, ">")
                    .replace(/&quot;/g, '"')
                    .replace(/&#39;/g, "'")
                    .trim();
                  return description;
                })()}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <StatTile
                icon={Users}
                value={`${packageData.totalStudents || "65"}+`}
                label="Total Students"
              />
              <StatTile
                icon={Clock}
                value={packageData.packageDuration || "6 Weeks"}
                label="Course Duration"
              />
            </div>
          </div>
        </div>

        {/* What You'll Learn */}
        <div className="mt-8">
          <h2 className="mb-4 text-xl font-semibold">What You'll Learn</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {[leftCol, rightCol].map((col, i) => (
              <ul key={i} className="space-y-3">
                {col.map((point, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-3 text-sm text-gray-300"
                  >
                    <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-[#F3E681]" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            ))}
          </div>
        </div>
      </div>

      {/* Available Courses */}
      <div className="mx-auto max-w-6xl">
        <h2 className="text-center text-xl font-semibold">Available Courses</h2>
        <p className="mb-6 text-center text-gray-400">
          Choose a course that fits your learning path - {courses.length} course
          {courses.length !== 1 ? "s" : ""} available
        </p>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {courses.map((course) => (
            <div
              key={course.id}
              className="rounded-xl border border-[#2A2A2A] bg-[#111111] shadow-sm transition hover:-translate-y-0.5 hover:border-[#CF9F41]"
            >
              <div className="relative h-40 w-full overflow-hidden rounded-t-xl">
                <img
                  src={
                    course.courseBanner ||
                    packageData.packageBanner ||
                    "https://via.placeholder.com/300x200/1f2937/ffffff?text=Course"
                  }
                  alt={course.courseName}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src =
                      "https://via.placeholder.com/300x200/1f2937/ffffff?text=Course";
                  }}
                />
                <span
                  className={`absolute right-3 top-3 rounded-full px-2 py-1 text-xs font-semibold ${goldGrad}`}
                >
                  Available
                </span>
              </div>

              <div className="p-4">
                <div className="mb-1 flex items-start justify-between gap-2">
                  <h3 className="text-sm font-semibold leading-5">
                    {course.courseName || "Digital Course"}
                  </h3>
                </div>
                <div className="mb-3 text-xs text-gray-400">
                  Available Now • Self Paced
                </div>
                <p className="mb-4 line-clamp-3 text-sm text-gray-300">
                  {(() => {
                    let description =
                      course.courseDescription ||
                      "Enhance your skills with this comprehensive course";
                    description = description
                      .replace(/<[^>]*>/g, "")
                      .replace(/&nbsp;/g, " ")
                      .replace(/&amp;/g, "&")
                      .replace(/&lt;/g, "<")
                      .replace(/&gt;/g, ">")
                      .replace(/&quot;/g, '"')
                      .replace(/&#39;/g, "'")
                      .trim();
                    return description;
                  })()}
                </p>
                <button
                  onClick={() => {
                    handleCourseClick(course.id);
                    setCourseId(course.id);
                  }}
                  className={`flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold ${goldGrad} hover:from-[#CF9F41] hover:to-[#B17E32]`}
                >
                  <PlayCircle className="h-4 w-4" />
                  Continue
                </button>
              </div>
            </div>
          ))}
        </div>

        {courses.length === 0 && (
          <div className="text-center text-gray-400">
            No courses available in this package.
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseList;
